import React, { useState, useEffect } from 'react';
import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    TableSortLabel,
    TextField,
    InputAdornment,
    makeStyles
} from "@material-ui/core";
import SearchIcon from '@material-ui/icons/Search';

// Create custom styles to reduce the padding in the TextField and set background color
const useStyles = makeStyles({
    filterInput: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            '& fieldset': {
                borderRadius: 0,
            },
            '& input': {
                padding: '4px 8px',
            },
        },
    },
    tableCell: {
        padding: 0,
        backgroundColor: 'white',
    },
});

const FacilityGeneralView = ({ facility, facilityType }) => {
    const classes = useStyles();

    // Add state for sorting
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('panpiNpi');
    const [filterText, setFilterText] = useState('');
    const [filteredNpis, setFilteredNpis] = useState([]);

    // Update filtered data when filter text or altNpiList changes
    useEffect(() => {
        if (facility.altNpiList && facility.altNpiList.length > 0) {
            const filtered = facility.altNpiList.filter(npi =>
                npi.panpiNpi.toLowerCase().includes(filterText.toLowerCase())
            );
            setFilteredNpis(filtered);
        } else {
            setFilteredNpis([]);
        }
    }, [filterText, facility.altNpiList]);

    // Handle sort request
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Sort function
    const sortData = (data, comparator) => {
        return data.slice().sort(comparator);
    };

    // Comparator function
    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    // Descending comparator
    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    };

    return (
        <div style={{ width: '100%' }}>
            {/* First row - Facility Name, TIN, NPI */}
            <Grid
                container
                justifyContent="space-between"
                style={{ paddingLeft: "1em", marginBottom: "1.5em" }}
            >
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <h6>{facilityType === 'H' ? 'Hospital Name:' : 'Ancillary Name:'}</h6>
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <h6>TIN:</h6>
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="right">
                    <h6>LOB:</h6>
                </Grid>
            </Grid>
            <Grid
                container
                justifyContent="space-between"
                style={{ paddingLeft: "1em", width: "100%", paddingTop: '.15em', marginTop: '-.7em', marginBottom: "2.5em" }}
            >
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <span>{facility.faclFacilityName || ''}</span>
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <span>{facility.faclTaxId || ''}</span>
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <span>{facility.fLob || ''}</span>
                </Grid>
            </Grid>

            {/* Second row - Alt NPI and LOB */}
            <Grid
                container
                justifyContent="space-between"
                style={{ paddingLeft: "1em", marginBottom: "1em" }}
            >
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <h6>NPI:</h6>
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    <h6>Alt NPI:</h6>
                </Grid>

                <Grid item style={{ width: "30%" }} justifyContent="right">
                </Grid>
            </Grid>
            <Grid
                container
                justifyContent="space-between"
                style={{ paddingLeft: "1em", width: "100%", paddingTop: '.15em', marginTop: '-.7em' }}
            >
                <Grid item style={{ width: "30%" }} justifyContent="right">
                    <span>{facility.prvNpi || ''}</span>
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="left">
                    {facility.altNpiList && facility.altNpiList.length > 0 ? (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            style={{
                                maxWidth: "250px",
                                maxHeight: facility.altNpiList.length > 5 ? "200px" : "none",
                                overflowY: facility.altNpiList.length > 5 ? "auto" : "visible"
                            }}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ backgroundColor: '#0f197d' }}>
                                            <TableSortLabel
                                                active={orderBy === 'panpiNpi'}
                                                direction={orderBy === 'panpiNpi' ? order : 'asc'}
                                                onClick={() => handleRequestSort('panpiNpi')}
                                            >
                                                Alt NPI
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className={classes.tableCell}>
                                            <TextField
                                                placeholder="Filter.."
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                className={classes.filterInput}
                                                value={filterText}
                                                onChange={(e) => setFilterText(e.target.value)}
                                                margin="none"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortData(filteredNpis, getComparator(order, orderBy)).map((npi, index) => (
                                        <TableRow key={npi.panpiId || index}>
                                            <TableCell>{npi.panpiNpi}</TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredNpis.length === 0 && (
                                        <TableRow>
                                            <TableCell align="center" style={{ color: 'gray' }}>
                                                {filterText ? "No matching NPIs" : "No Alt NPIs found"}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <span></span>
                    )}
                </Grid>
                <Grid item style={{ width: "30%" }} justifyContent="right">
                </Grid>
            </Grid>
            <div style={{ marginBottom: "2em" }} />
        </div>
    );
};

export default FacilityGeneralView;